


























































































































































































































































































import ALPage from '@/components/core/Page/ALPage.vue';
import CountryFlag from 'vue-country-flag';
import ErrorAlert from '@/components/common/ErrorAlert.vue';
import ConfirmationDialog from '@/components/common/ConfirmationDialog.vue';
import DeleteConfirmationDialog from '@/components/common/DeleteConfirmationDialog.vue';
import WorkflowFormSubmissionDialog from '@/components/workflow-form/WorkflowFormSubmissionDialog.vue';
import WorkflowFormSubmissionSearchFilter from '@/components/workflow-form/WorkflowFormSubmissionSearchFilter.vue';
import WorkflowFormSubmissionActivityDialog from '@/components/workflow-form/WorkflowFormSubmissionActivityDialog.vue';
import WorkflowStateStatusDisplay from '@/components/workflows/WorkflowStateStatusDisplay.vue';

import { mapActions, mapGetters } from 'vuex';
import {
  FormTemplateStateEnum,
  IWorkflowFormSubmission,
  IWorkflowFormTemplate,
  IWorkflowFormTemplateRevision,
  WorkflowFormTemplateConfigHeaderTypeEnum
} from '@/models';
import { ErrorManager } from '@/models/error';

import moment from 'moment';
import { merge, omit } from 'lodash';

import { IAttachment } from '@/models/attachment/attachment.types';
import {
  IWorkflowFormSubmissionFetchParams,
  IFetchFormTemplatesParams
} from '@/services';

import mixins from '@/utils/mixins';
import Functions from '@/mixins/functions';
import auth from '@/mixins/auth';
import SnackBarMixin from '@/mixins/snackbar';
import { AxiosError } from 'axios';
import { createDownloadFile, setErrorFromAxiosError } from '@/utils/helpers';
import { ISystemError } from '@/models/error/error.types';
import { DataTableHeader } from 'vuetify';
import {
  createPdf,
  convertBooleanStringToNullableBoolean
} from '@/utils/helpers';

import DetailOptionsButton from '@/components/common/DetailOptionsButton.vue';
import UrgentCard from '@/components/common/UrgentCard.vue';

const baseMixins = mixins(Functions, auth, SnackBarMixin);

interface Options extends InstanceType<typeof baseMixins> {
  /* eslint-disable-next-line camelcase */
  $refs: {
    workflowFormSubmissionDialog: InstanceType<
      typeof WorkflowFormSubmissionDialog
    >;

    workflowFormSubmissionActivityDialog: InstanceType<
      typeof WorkflowFormSubmissionActivityDialog
    >;
  };
}

export default baseMixins
  .extend<Options>({
    functional: false
  })
  .extend({
    components: {
      ALPage,
      CountryFlag,
      ConfirmationDialog,
      DeleteConfirmationDialog,
      DetailOptionsButton,
      ErrorAlert,
      WorkflowFormSubmissionDialog,
      WorkflowFormSubmissionSearchFilter,
      WorkflowFormSubmissionActivityDialog,
      WorkflowStateStatusDisplay,
      UrgentCard
    },

    props: {
      id: { type: [String, Number] }
    },

    data() {
      return {
        loading: false,
        isSearchFilterActive: false,
        isExportSubmissionsLoading: false,

        errorMessage: '',
        detailErrorMessage: '',

        params: {
          search: '',
          page: 1
        } as IWorkflowFormSubmissionFetchParams,

        sortBy: 'id',
        sortDesc: true,

        selectedWorkflowFormSubmission: null as null | IWorkflowFormSubmission,
        deleteError: null as null | ISystemError,

        deleteInProgress: false,
        deleteDialog: false
      };
    },

    computed: {
      ...mapGetters({
        getWorkflowFormTemplateRevisionById:
          'workflowFormTemplate/getWorkflowFormTemplateRevisionById',
        workflowFormTemplateRevisions:
          'workflowFormTemplate/getWorkflowFormTemplateRevisions',

        workflowFormSubmissions: 'workflowForm/getWorkflowFormSubmissions',
        workflowFormTemplates: 'workflowFormTemplate/getWorkflowFormTemplates',
        numSubmissions: 'workflowForm/getWorkflowFormSubmissionCount',
        getWorkflowFormTemplatesWithPublishedRevisionById:
          'workflowFormTemplate/getWorkflowFormTemplatesWithPublishedRevisionById'
      }),

      selectedFormTemplate(): IWorkflowFormTemplate | null {
        return this.getWorkflowFormTemplatesWithPublishedRevisionById(
          this.templateDetailId
        );
      },

      publishedWorkflowFormRevision(): IWorkflowFormTemplateRevision | null {
        return (
          this.workflowFormTemplateRevisions.find(
            (revision: IWorkflowFormTemplateRevision) => revision.isPublished()
          ) ?? null
        );
      },

      headers() {
        let defaultHeaders: DataTableHeader[] = [
          {
            text: '#',
            sortable: false,
            value: 'series_id'
          },

          {
            text: 'Requester',
            sortable: false,
            value: 'owner'
          }
        ];

        if (this.selectedFormTemplate) {
          defaultHeaders.push(
            ...this.selectedFormTemplate.config.headers
              .filter(
                header =>
                  header.type === WorkflowFormTemplateConfigHeaderTypeEnum.Table
              )
              .map(header => {
                return {
                  text: header.display_label,
                  sortable: false,
                  align: 'start',
                  value: `meta_fields.${header.meta_label}`,
                  width: header.width ? header.width : ''
                } as DataTableHeader;
              })
          );
        }

        defaultHeaders.push(
          ...([
            {
              text: 'Last Modified',
              sortable: false,
              value: 'updated_at'
            },

            { text: '', sortable: false, value: 'is_urgent', width: 60 },
            {
              text: 'Status',
              sortable: false,
              align: 'center',
              value: 'state'
            },
            {
              text: '',
              sortable: false,
              align: 'center',
              value: 'actions'
            }
          ] as DataTableHeader[])
        );

        return defaultHeaders;
      },

      extraFiltersApplied(): boolean {
        return Object.keys(omit(this.$route.query, ['page', 'search'])).some(
          v => !!v
        );
      },

      templateDetailId(): number {
        return Number(this.$route.params.id);
      }
    },

    watch: {
      templateDetailId(id) {
        this.params = {
          page: 1,
          form__template: id
        };
        this.refreshWorkflowFormState(this.params);
      }
    },

    async mounted() {
      // update `this.params` from the route query object.
      // omitting `search` and `page` as they are already set in the data vue option
      merge(this.params, omit(this.$route.query, ['page', 'search']));

      if (this.$route.query.is_urgent) {
        this.params.is_urgent = convertBooleanStringToNullableBoolean(
          this.$route.query.is_urgent as string
        );
      }
      this.refreshWorkflowFormState(this.params);
    },

    methods: {
      moment,

      ...mapActions({
        fetchWorkflowFormSubmissions:
          'workflowForm/fetchWorkflowFormSubmissions',
        deleteWorkflowFormSubmission:
          'workflowForm/deleteWorkflowFormSubmission',
        fetchWorkflowFormTemplateRevisions:
          'workflowFormTemplate/fetchWorkflowFormTemplateRevisions',
        updateWorkflowFormSubmissionState:
          'workflowForm/updateWorkflowFormSubmissionState',
        exportWorkflowFormSubmissionPDF:
          'workflowForm/exportWorkflowFormSubmissionPDF',
        exportWorkflowFormSubmissionXLSX:
          'workflowForm/exportWorkflowFormSubmissionXLSX',
        fetchWorkflowFormTemplatesWithPublishedRevisions:
          'workflowFormTemplate/fetchWorkflowFormTemplatesWithPublishedRevisions'
      }),

      isOwner(submission: IWorkflowFormSubmission): boolean {
        return this.getCurrentUserFullName == submission.owner;
      },

      isCurrentUserAssignee(submission: IWorkflowFormSubmission): boolean {
        return this.currentUser.id == submission.assignee?.id;
      },

      goToDetailPage(submission: IWorkflowFormSubmission) {
        this.$router.push({
          name: 'workflow-form-request-detail',
          params: {
            templateId: String(this.id),
            id: String(submission.id)
          }
        });
      },

      openActivitiesLogDialog(request: IWorkflowFormSubmission) {
        this.$refs.workflowFormSubmissionActivityDialog.open(request);
      },

      async refreshWorkflowFormState(
        params: IWorkflowFormSubmissionFetchParams
      ) {
        this.clearAllErrors();
        this.loading = true;
        try {
          await this.searchWorkflowFormSubmissions(params);

          await this.fetchWorkflowFormTemplateRevisions({
            templateId: this.templateDetailId,
            params: { state: FormTemplateStateEnum.Published }
          });
        } catch (err) {
          const error = err as AxiosError;
          if (error.response) {
            if (error.response.data.detail) {
              this.errorMessage = error.response.data.detail;
            }
          }
          this.errorMessage = ErrorManager.extractApiError(error);
        } finally {
          this.loading = false;
        }
      },

      exportPdf(submission: IWorkflowFormSubmission) {
        this.loading = true;
        this.exportWorkflowFormSubmissionPDF(submission.id)
          .then((data: Blob) => {
            createPdf(
              data,
              `${
                (submission.form as IWorkflowFormTemplateRevision).template!
                  .title
              } #${
                (submission.form as IWorkflowFormTemplateRevision).template!
                  .prefix
              }${submission.number}`
            );
          })
          .catch(responseError => {
            this.errorMessage = ErrorManager.extractApiError(responseError);
            if (responseError.response) {
              if (responseError.response.data.detail) {
                this.detailErrorMessage = responseError.response.data.detail;
              }
            }
          })
          .finally(() => {
            this.loading = false;
          });
      },

      exportXLSX() {
        this.isExportSubmissionsLoading = true;
        this.exportWorkflowFormSubmissionXLSX(this.params)
          .then((data: Blob) => {
            const fileName = `${
              this.selectedFormTemplate!.title
            }-${moment().format('YYYY-MM-DD-HH-mm-ss')}`;
            createDownloadFile(data, `${fileName}.xlsx`);
          })
          .catch(responseError => {
            this.errorMessage = ErrorManager.extractApiError(responseError);
            if (responseError.response) {
              if (responseError.response.data.detail) {
                this.detailErrorMessage = responseError.response.data.detail;
              }
            }
          })
          .finally(() => {
            this.isExportSubmissionsLoading = false;
          });
      },

      openFormDialog(templateId: number, submission?: IWorkflowFormSubmission) {
        const revision = this.publishedWorkflowFormRevision;

        if (revision) {
          this.$refs.workflowFormSubmissionDialog.open(revision, submission);
        }
      },

      refreshSearchFilter(params: IWorkflowFormSubmissionFetchParams) {
        this.clearAllErrors();

        this.loading = true;
        this.searchWorkflowFormSubmissions({
          page: 1,
          search: this.params.search,
          ...params
        }).then(() => {
          // update params objects
          this.params = omit(params, 'search', 'page');
          this.params.page = 1;
        });
      },

      onSearch(search: string) {
        // Explicit search trigger; reset to page 1 and fetch without relying on watchers
        this.clearAllErrors();
        this.params.page = 1;
        this.debounceFetchWorkflowFormSubmissions({
          ...this.params,
          search: search
        });
      },

      searchWorkflowFormSubmissions(
        params: IWorkflowFormSubmissionFetchParams
      ) {
        if (this.id) {
          // add template detail query to all searches
          params.form__template = this.templateDetailId;
        }

        /* this will update the URL query based on param values. */
        this.$router
          .replace({
            name: 'workflow-form-submission-list',
            query: params as Record<string, any>,
            params: { id: `${this.id}` }
          })
          .catch((e: Error) => e); //catch navigation duplication error

        return this.fetchWorkflowFormSubmissions(params)
          .catch(error => {
            this.errorMessage = ErrorManager.extractApiError(error);
            if (error.response) {
              if (error.response.data.detail) {
                this.detailErrorMessage = error.response.data.detail;
              }
            }
          })
          .finally(() => {
            this.loading = false;
          });
      },

      onUpdatePage(newPage: number) {
        if (newPage != this.params.page) {
          this.loading = true;
          this.searchWorkflowFormSubmissions({
            ...this.params,
            page: newPage
          }).then(() => {
            this.params.page = newPage;
          });
        }
      },

      async debounceFetchWorkflowFormSubmissions(
        params: IWorkflowFormSubmissionFetchParams
      ) {
        this.clearAllErrors();
        this.loading = true;

        return this.debounce(this.searchWorkflowFormSubmissions, 200)(params);
      },

      clearAllErrors(): void {
        this.deleteError = null;
        this.errorMessage = '';
        this.detailErrorMessage = '';
      },

      deleteRecord(submission: IWorkflowFormSubmission) {
        this.clearAllErrors();
        this.selectedWorkflowFormSubmission = submission;
        this.deleteDialog = true;
      },

      cancelSubmissionDeleteDialog() {
        this.deleteDialog = true;
        this.selectedWorkflowFormSubmission = null;
      },

      removeWorkflowFormSubmission(submission: IWorkflowFormSubmission) {
        this.clearAllErrors();
        this.deleteInProgress = true;

        this.deleteWorkflowFormSubmission(submission.id)
          .then(() => {
            this.deleteDialog = false;
          })
          .catch(error => {
            this.deleteError = setErrorFromAxiosError(error);
          })
          .finally(() => {
            this.deleteInProgress = false;
          });
      },

      downloadAttachment(attachment: IAttachment) {
        this.clearAllErrors();
        this.$services.fileLoader
          .downloadFile(attachment.file as string)
          .then(data => {
            createDownloadFile(data, attachment.filename);
          })
          .then(() => {
            this.createSnack({
              color: 'success darken-1',
              type: 'download',
              right: true,
              useBtn: false,
              msg: `Downloaded file: '${attachment.filename}'`,
              btnText: 'Close'
            });
          })
          .catch(error => {
            const err = setErrorFromAxiosError(error);
            this.createSnack({
              color: 'error',
              type: 'error',
              right: true,
              useBtn: false,
              msg: err.detail || err.message,
              btnText: 'Close'
            });
          });
      }
    }
  });
